// Progressive enhancement: native touch scrolling remains the mobile fallback.
(() => {
  function initialize(root) {
    if (root.dataset.marqueeReady) return;
    const track = root.querySelector('.grid');
    const button = root.querySelector('.brand-motion-toggle');
    if (!track || !button) return;
    root.dataset.marqueeReady = 'true';
    const originals = [...track.children];
    const clones = originals.map((item) => {
      const clone = item.cloneNode(true);
      clone.dataset.marqueeClone = '';
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      clone.querySelectorAll('a,button').forEach((el) => el.tabIndex = -1);
      return clone;
    });
    const desktop = matchMedia('(min-width: 990px)');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const controller = new AbortController();
    const listen = (target, event, callback) => target.addEventListener(event, callback, {signal: controller.signal});
    let paused = reduced.matches, hovering = false, focused = false;
    let visible = !('IntersectionObserver' in window);
    let position = 0, last = null, frame = null, distance = null;
    let intersection, resize, disposed = false;

    function stop() {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = last = null;
    }
    function cleanup() {
      disposed = true;
      stop();
      controller.abort();
      intersection?.disconnect();
      resize?.disconnect();
      clones.forEach((clone) => clone.remove());
      root.classList.remove('brand-marquee-enabled');
      delete root.dataset.marqueeReady;
    }
    function active() {
      return desktop.matches && !paused && !hovering && !focused && visible && !document.hidden;
    }
    function sync() {
      if (disposed) return;
      if (!root.isConnected) { cleanup(); return; }
      if (!active()) { stop(); return; }
      if (frame === null) frame = requestAnimationFrame(tick);
    }
    function configure() {
      stop();
      clones.forEach((clone) => clone.remove());
      if (desktop.matches) track.append(...clones);
      root.classList.toggle('brand-marquee-enabled', desktop.matches);
      position = track.scrollLeft = 0;
      distance = null;
      sync();
    }
    function tick(now) {
      frame = null;
      if (!root.isConnected || !active()) { sync(); return; }
      const elapsed = last === null ? 0 : Math.min(now - last, 50);
      last = now;
      if (distance === null) distance = clones[0]?.offsetLeft - originals[0]?.offsetLeft;
      if (distance > 0) {
        position = (position + elapsed * 0.025) % distance;
        track.scrollLeft = position;
      }
      sync();
    }

    listen(button, 'click', () => {
      paused = !paused;
      button.setAttribute('aria-pressed', String(paused));
      position = track.scrollLeft;
      sync();
    });
    listen(track, 'pointerenter', () => { hovering = true; sync(); });
    listen(track, 'pointerleave', () => { hovering = false; position = track.scrollLeft; sync(); });
    listen(track, 'focusin', () => { focused = true; sync(); });
    listen(track, 'focusout', (event) => {
      focused = track.contains(event.relatedTarget);
      position = track.scrollLeft;
      sync();
    });
    listen(desktop, 'change', configure);
    listen(reduced, 'change', () => {
      paused = reduced.matches;
      button.setAttribute('aria-pressed', String(paused));
      sync();
    });
    listen(document, 'visibilitychange', sync);
    listen(document, 'shopify:section:unload', (event) => {
      if (event.target.contains(root)) cleanup();
    });
    button.setAttribute('aria-pressed', String(paused));
    configure();
    if ('IntersectionObserver' in window) {
      intersection = new IntersectionObserver((entries) => {
        visible = entries[0].isIntersecting;
        sync();
      });
      intersection.observe(root);
    }
    if ('ResizeObserver' in window) {
      resize = new ResizeObserver(() => { distance = null; });
      resize.observe(track);
    } else {
      listen(window, 'resize', () => { distance = null; });
    }
  }
  document.querySelectorAll('[data-brand-marquee]').forEach(initialize);
  if (window.Shopify?.designMode && !document.documentElement.dataset.marqueeEditorReady) {
    document.documentElement.dataset.marqueeEditorReady = 'true';
    document.addEventListener('shopify:section:load', (event) => {
      event.target.querySelectorAll('[data-brand-marquee]').forEach(initialize);
    });
  }
})();
